/**
 * Org-agnostic multi-tenant helpers.
 *
 * Doctrine: ROLE_ARCHITECTURE.md
 * - Users can belong to multiple orgs
 * - Optional "primary org" per org_type (display only, zero competitive effect)
 * - Roles scoped per org: owner / admin / coach / member
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import type {
  Organization,
  OrganizationMembership,
  OrgType,
  OrgRole,
} from '@/types/database';

// Re-export provisioning helpers so callers can import from one place.
export {
  provisionClubOrg,
  resolveClubOwnerOrg,
  reconcileClubOrg,
} from '@/lib/orgProvisioning';

const log = createLogger('ORG');

/**
 * Fetch all active memberships for the current user.
 */
export async function fetchMyMemberships(profileId: string): Promise<OrganizationMembership[]> {
  const { data, error } = await supabase
    .from('organization_memberships')
    .select('*')
    .eq('user_id', profileId)
    .eq('status', 'active')
    .order('created_at', { ascending: true });

  if (error) {
    log.warn('[Orgs] Failed to load memberships', error.message);
    return [];
  }
  return (data as OrganizationMembership[]) ?? [];
}

/**
 * Fetch the organization record for a given org ID.
 */
export async function fetchOrganization(orgId: string): Promise<Organization | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('id', orgId)
    .maybeSingle();

  if (error) {
    log.warn('[Orgs] Failed to load organization', error.message);
    return null;
  }
  return data as Organization | null;
}

/**
 * Fetch organizations by IDs.
 */
export async function fetchOrganizations(orgIds: string[]): Promise<Organization[]> {
  if (!orgIds || orgIds.length === 0) return [];
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .in('id', orgIds);

  if (error) {
    log.warn('[Orgs] Failed to load organizations', error.message);
    return [];
  }
  return (data as Organization[]) ?? [];
}

/**
 * Get the user's primary org for a given org_type from a memberships array.
 * Returns null if no primary is set.
 *
 * Per ROLE_ARCHITECTURE.md: "Primary org affects only display context.
 * Primary org has zero effect on scoring, matchmaking, or eligibility."
 */
export function getPrimaryOrg(
  memberships: OrganizationMembership[],
  orgType: OrgType,
  organizations: Organization[],
): Organization | null {
  const primaryMembership = memberships.find(
    (m) => m.is_primary_for_type && organizations.find((o) => o.id === m.org_id)?.org_type === orgType,
  );
  if (!primaryMembership) return null;
  return organizations.find((o) => o.id === primaryMembership.org_id) ?? null;
}

/**
 * Get the user's role in a specific org from a memberships array.
 * Returns null if user is not a member.
 */
export function getOrgRole(
  memberships: OrganizationMembership[],
  orgId: string,
): OrgRole | null {
  const membership = memberships.find((m) => m.org_id === orgId && m.status === 'active');
  return membership?.role ?? null;
}

/**
 * Check if user has admin-level access (owner or admin) in an org.
 */
export function isOrgAdmin(
  memberships: OrganizationMembership[],
  orgId: string,
): boolean {
  const role = getOrgRole(memberships, orgId);
  return role === 'owner' || role === 'admin';
}

/**
 * Fetch the user's active role in a specific org from the database.
 * Returns null if the user has no active membership.
 *
 * Use this when you don't already have a memberships array loaded
 * (e.g., in invite helpers, roster screens, standalone permission checks).
 */
export async function getOrgRoleForUser(orgId: string, userId: string): Promise<OrgRole | null> {
  const { data, error } = await supabase
    .from('organization_memberships')
    .select('role')
    .eq('org_id', orgId)
    .eq('user_id', userId)
    .eq('status', 'active')
    .maybeSingle();

  if (error) {
    log.warn('[Orgs] getOrgRoleForUser query error', error.message);
    return null;
  }
  return (data?.role as OrgRole) ?? null;
}

/**
 * Fetch all direct child organizations of a parent org.
 *
 * Phase 1: uses parent_organization_id column added in migration
 * 20260405000000_add_parent_organization_id.sql.
 *
 * Note: Does NOT recursively fetch grandchildren. For school → team hierarchies
 * a single level of depth is sufficient for V1.
 */
export async function getChildOrganizations(parentOrgId: string): Promise<Organization[]> {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .eq('parent_organization_id', parentOrgId)
    .order('name', { ascending: true });

  if (error) {
    log.warn('[Orgs] Failed to load child organizations', error.message);
    return [];
  }
  return (data as Organization[]) ?? [];
}
